import { useEffect, useRef } from "react";
import Modal from "../../components/Modal";
import Icon from "../../components/Icon";

/*
  NotificationsPanel — הפאנל שנפתח בלחיצה על הפעמון 🔔 במסך הבית.
  מציג את כל ההתראות עם אייקון מתאים; אפשר לסמן כל אחת כ"נקראה" (או את כולן),
  והן נשמרות מעומעמות. הפעמון סופר רק את מה שעוד לא נקרא.
  אם לא נוגעים בפאנל 2 שניות — הוא נסגר לבד (חוסר פעילות).
*/
// כל סוג התראה → שם אייקון-קו אחיד (Icon). ברירת מחדל: פעמון.
const TYPE_ICONS = {
  payments: "wallet",
  unpaid: "wallet",
  birthday: "cake",
  holiday: "star",
  event: "calendar",
  gift: "gift",
  team: "users",
};

/* אחרי כמה זמן של חוסר פעילות לסגור את הפאנל לבד. */
const IDLE_CLOSE_MS = 2000;

/* פעילויות שמאפסות את שעון חוסר-הפעילות (מגע, גלילה, עכבר, מקלדת). */
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "touchmove",
  "scroll",
  "wheel",
  "click",
];

function NotificationsPanel({
  isOpen,
  notifications,
  onClose,
  onMarkRead,
  onMarkAllRead,
}) {
  // ref כדי להשתמש ב-onClose העדכני בלי להריץ מחדש את האפקט בכל רינדור
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // סגירה אוטומטית אחרי 15 שניות בלי פעילות; כל פעילות מאפסת את השעון.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let timer;
    const resetTimer = () => {
      clearTimeout(timer);
      timer = setTimeout(() => onCloseRef.current(), IDLE_CLOSE_MS);
    };
    ACTIVITY_EVENTS.forEach((evt) =>
      window.addEventListener(evt, resetTimer, true)
    );
    resetTimer(); // מתחילים את השעון ברגע הפתיחה
    return () => {
      clearTimeout(timer);
      ACTIVITY_EVENTS.forEach((evt) =>
        window.removeEventListener(evt, resetTimer, true)
      );
    };
  }, [isOpen]);

  const hasUnread = notifications.some((n) => !n.read);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="ההתראות שלך">
      {notifications.length === 0 ? (
        <p className="notifications__empty">אין התראות כרגע — הכל מסודר! 🎉</p>
      ) : (
        <>
          {hasUnread && (
            <button
              type="button"
              className="notifications__mark-all"
              onClick={onMarkAllRead}
            >
              סמן הכל כנקרא
            </button>
          )}
          <ul className="notifications">
            {notifications.map((n) => (
              <li
                key={n.id}
                className={`notifications__item notifications__item--${n.type}${
                  n.read ? " notifications__item--read" : ""
                }`}
              >
                <span className="notifications__icon" aria-hidden="true">
                  <Icon name={TYPE_ICONS[n.type] || "bell"} size={20} />
                </span>
                <span className="notifications__message">{n.message}</span>
                {n.read ? (
                  <span className="notifications__read-label">
                    <Icon name="check" size={14} /> נקרא
                  </span>
                ) : (
                  <button
                    type="button"
                    className="notifications__mark"
                    aria-label={`סמן כנקרא: ${n.message}`}
                    onClick={() => onMarkRead(n.id)}
                  >
                    סמן כנקרא
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Modal>
  );
}

export default NotificationsPanel;
